/* eslint-disable no-console */
import { OverlayModule } from '@angular/cdk/overlay';
import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { MatButton, MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatToolbarModule } from '@angular/material/toolbar';
import { RouterModule } from '@angular/router';
import { format } from 'date-fns';
import { filter, map, Observable, Subscription, timer } from 'rxjs';

import { AboutComponent } from './about/about.component';
import { BaccaratTableComponent } from './baccarat-table/baccarat-table.component';
import { BurnCardsComponent } from './burn-cards/burn-cards.component';
import { ChatComponent } from './chat/chat.component';
import { ClosedTableComponent } from './closed-table/closed-table.component';
import { EnvironmentResolver } from './core/environment-resolver';
import { CountDownComponent } from './count-down/count-down.component';
import { ErrorPanelComponent } from './error-panel/error-panel.component';
import {
    BaccaratOutcome,
    BaccaratPhase,
    DealerConsoleAction,
    FlagStateType,
    NotificationCode,
    NotificationSeverity
} from './models/communication/baccarat-enums.model';
import { BaccaratEventType, NotificationEvent } from './models/communication/baccarat-events.model';
import { BaccaratState } from './models/communication/baccarat-state.model';
import { PhaseTitleComponent } from './phase-title/phase-title.component';
import { RoundVoidedComponent } from './round-voided/round-voided.component';
import { DealerService } from './services/dealer.service';
import { ShoeChangeComponent } from './shoe-change/shoe-change.component';
import { WaitingForPlayersComponent } from './waiting-for-players/waiting-for-players.component';

@Component({
    standalone: true,
    imports: [
        CommonModule,
        RouterModule,
        BaccaratTableComponent,
        BurnCardsComponent,
        CountDownComponent,
        ClosedTableComponent,
        WaitingForPlayersComponent,
        ShoeChangeComponent,
        PhaseTitleComponent,
        MatButtonModule,
        MatIconModule,
        MatMenuModule,
        MatSnackBarModule,
        MatToolbarModule,
        ChatComponent,
        RoundVoidedComponent,
        ErrorPanelComponent,
        OverlayModule,
        AboutComponent
    ],
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrl: './app.component.scss'
})
export class AppComponent implements OnInit, OnDestroy {
    @ViewChild('aboutTemplate', { static: false }) aboutTemplate?: TemplateRef<AboutComponent>;

    protected readonly BaccaratOutcome = BaccaratOutcome;
    protected readonly BaccaratPhase = BaccaratPhase;

    protected time$: Observable<string> = this.createTimeObservable();
    protected state$!: Observable<BaccaratState>;
    protected version: string;
    protected isVideoHidden = false;

    private subscription?: Subscription;

    constructor(
        private readonly environment: EnvironmentResolver,
        private readonly dealerService: DealerService,
        private readonly snackBar: MatSnackBar,
        private readonly dialog: MatDialog
    ) {
        this.version = this.environment.getEnvironment().version;
    }

    ngOnInit(): void {
        this.state$ = this.dealerService.getStateObservable().pipe(filter((state): state is BaccaratState => !!state));

        this.subscription = this.dealerService.getEventsObservable().subscribe((event) => {
            if (event.name === BaccaratEventType.CutCardDrawn) {
                this.handleCutCardDrawnEvent();
            } else if (event.name === BaccaratEventType.Notification) {
                this.handleNotification(event.data as NotificationEvent);
            }
        });
    }

    ngOnDestroy(): void {
        if (this.subscription) {
            this.subscription.unsubscribe();
        }
    }

    async onFlagGameClick(button: MatButton): Promise<void> {
        await this.onActionClick(button, FlagStateType.RoundFlagged);
    }

    async onFreeGameClick(button: MatButton): Promise<void> {
        await this.onActionClick(button, FlagStateType.RoundBetsFree);
    }

    async onCallSupervisorClick(button: MatButton): Promise<void> {
        await this.onActionClick(button, FlagStateType.SupervisorCalled);
    }

    async onCloseTableClick(button: MatButton): Promise<void> {
        await this.onActionClick(button, FlagStateType.TableClosingRequested);
    }

    async onEnableChatClick(button: MatButton): Promise<void> {
        await this.onActionClick(button, FlagStateType.ChatEnabled);
    }

    async onDisableChatClick(button: MatButton): Promise<void> {
        await this.onActionClick(button, FlagStateType.ChatEnabled, false);
    }

    async onChangeShoeClick(button: MatButton): Promise<void> {
        try {
            this.setButtonDisabledState(button, true);
            await this.dealerService.sendDealerActionRequest(DealerConsoleAction.ChangeShoe);
        } catch (error) {
            this.setButtonDisabledState(button, false);
            console.error(error);
            this.snackBar.open('Action failed', 'Close');
        }
    }

    protected showAboutDialog(): void {
        if (this.aboutTemplate) {
            this.dialog.open(this.aboutTemplate);
        }
    }

    private async onActionClick(button: MatButton, flag: FlagStateType, state = true): Promise<void> {
        try {
            this.setButtonDisabledState(button, true);
            await this.dealerService.sendSetFlagStateRequest(flag, state);
            if (flag === FlagStateType.ChatEnabled) {
                this.setButtonDisabledState(button, false);
            }
        } catch (error) {
            this.setButtonDisabledState(button, false);
            console.error(error);
            this.snackBar.open($localize`Action failed`, $localize`Close`);
        }
    }

    private setButtonDisabledState(button: MatButton, disabled: boolean): void {
        // eslint-disable-next-line no-param-reassign
        button.disabled = disabled;
    }

    private createTimeObservable(): Observable<string> {
        // eslint-disable-next-line @typescript-eslint/no-magic-numbers
        return timer(0, 1000).pipe(map(() => format(new Date(), 'HH:mm:ss')));
    }

    private handleCutCardDrawnEvent(): void {
        this.snackBar.open('Cut card drawn', $localize`Close`, { duration: 3000 });
    }

    private handleNotification(event: NotificationEvent): void {
        const config = { panelClass: event.severity.toLowerCase() };
        if (event.severity === NotificationSeverity.Info) {
            this.snackBar.open(this.getNotificationText(event.code), undefined, { ...config, duration: 3000 });
        } else {
            this.snackBar.open(this.getNotificationText(event.code), $localize`Close`, config);
        }
    }

    private getNotificationText(code: NotificationCode) {
        switch (code) {
            case NotificationCode.AuthenticationFailed:
                return $localize`Authentication failed!`;
            case NotificationCode.AuthenticationSuccess:
                return $localize`Successfully authenticated!`;
            case NotificationCode.CroupierChangeInvalidState:
                return $localize`Croupier change not allowed in this game state.`;
            case NotificationCode.DuplicateCard:
                return $localize`Same round, same card detected.`;
            case NotificationCode.FlagFailed:
                return $localize`Flag  failed, round not marked.`;
            case NotificationCode.InvalidCard:
                return $localize`Invalid card or card not recognized.`;
            case NotificationCode.InvalidCroupierId:
                return $localize`Invalid croupier ID for this table!`;
            case NotificationCode.ScannerDisconnected:
                return $localize`Card scanner is disconnected!`;
            case NotificationCode.ScannerConnected:
                return $localize`Card scanner is connected and ready.`;
            default:
                // eslint-disable-next-line no-console
                console.warn(`Unknown notification code: ${code as string}`);
                return code;
        }
    }
}
